import { Button, Checkbox, Select, Spin, message } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import CompanyApi from '../../api/routes/CompanyApi';
import {
    ProjectListModel,
    getAllProjects,
    getCompanyProjectAccess,
    setCompanyProjectAccess,
} from '../../api/documentProjectApi';

const { Option } = Select;

interface Company {
    id: number;
    name: string;
}

const CompanyProjectAccess: FunctionComponent = () => {
    const [companies, setCompanies] = useState<Company[]>([]);
    const [projects, setProjects] = useState<ProjectListModel[]>([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
    const [checkedProjectIds, setCheckedProjectIds] = useState<number[]>([]);
    const [loadingCompanies, setLoadingCompanies] = useState(true);
    const [loadingAccess, setLoadingAccess] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        Promise.all([
            CompanyApi.fetchCompanies().then((r) => r.data as Company[]),
            getAllProjects(),
        ])
            .then(([cos, projs]) => {
                setCompanies(cos);
                setProjects(projs);
                if (cos.length > 0) setSelectedCompanyId(cos[0].id);
            })
            .catch(() => message.error('Failed to load data'))
            .finally(() => setLoadingCompanies(false));
    }, []);

    useEffect(() => {
        if (selectedCompanyId == null) return;
        setLoadingAccess(true);
        getCompanyProjectAccess(selectedCompanyId)
            .then(setCheckedProjectIds)
            .catch(() => message.error('Failed to load access'))
            .finally(() => setLoadingAccess(false));
    }, [selectedCompanyId]);

    const toggleProject = (projectId: number, checked: boolean) => {
        setCheckedProjectIds((prev) =>
            checked ? [...prev, projectId] : prev.filter((id) => id !== projectId),
        );
    };

    const handleSave = async () => {
        if (selectedCompanyId == null) return;
        setSaving(true);
        try {
            await setCompanyProjectAccess(selectedCompanyId, checkedProjectIds);
            message.success('Access saved. Users from this company will see the selected projects.');
        } catch {
            message.error('Failed to save access.');
        } finally {
            setSaving(false);
        }
    };

    const selectedCompany = companies.find((c) => c.id === selectedCompanyId);

    if (loadingCompanies) {
        return (
            <div style={{ padding: 40, textAlign: 'center' }}>
                <Spin />
            </div>
        );
    }

    return (
        <div style={{ display: 'grid', gap: 20, maxWidth: 860 }}>
            {/* Company picker */}
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, background: '#fff' }}>
                <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#111827' }}>Select Company</h3>
                <Select
                    style={{ width: '100%' }}
                    value={selectedCompanyId ?? undefined}
                    onChange={(val) => setSelectedCompanyId(val)}
                    placeholder="Select a company…"
                >
                    {companies.map((c) => (
                        <Option key={c.id} value={c.id}>
                            {c.name}
                        </Option>
                    ))}
                </Select>
            </div>

            {/* Project checkboxes */}
            {selectedCompanyId != null && (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, background: '#fff' }}>
                    <h3 style={{ margin: '0 0 4px', fontSize: 15, color: '#111827' }}>
                        Projects visible to <span style={{ color: '#2563eb' }}>{selectedCompany?.name}</span>
                    </h3>
                    <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6b7280' }}>
                        Tick the projects whose documents all users of this company can view.
                    </p>

                    {loadingAccess ? (
                        <Spin size="small" />
                    ) : projects.length === 0 ? (
                        <div style={{ color: '#9ca3af' }}>No projects found.</div>
                    ) : (
                        <div style={{ display: 'grid', gap: 10 }}>
                            {projects.map((p) => (
                                <label
                                    key={p.id}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 10,
                                        padding: '10px 12px',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: 6,
                                        cursor: 'pointer',
                                        background: checkedProjectIds.includes(p.id) ? '#eff6ff' : '#fff',
                                        borderColor: checkedProjectIds.includes(p.id) ? '#bfdbfe' : '#e5e7eb',
                                    }}
                                >
                                    <Checkbox
                                        checked={checkedProjectIds.includes(p.id)}
                                        onChange={(e) => toggleProject(p.id, e.target.checked)}
                                    />
                                    <span style={{ fontWeight: 500, color: '#111827' }}>{p.name}</span>
                                </label>
                            ))}
                        </div>
                    )}

                    <Button
                        type="primary"
                        loading={saving}
                        onClick={handleSave}
                        style={{ marginTop: 20, width: 'fit-content' }}
                        disabled={loadingAccess}
                    >
                        Save Access
                    </Button>
                </div>
            )}
        </div>
    );
};

export default CompanyProjectAccess;
